import { InterviewForm } from "@/components/admin/interview-form";
import { LockedPipelineView, PipelineConfigurator } from "@/components/admin/pipeline-configurator";
import { StageSelect } from "@/components/admin/stage-select";
import { SectionHeader } from "@/components/dashboard/section-header";
import { ErrorState, InlineError } from "@/components/dashboard/state";
import { StatusPill } from "@/components/dashboard/status-pill";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminApi } from "@/lib/admin-api";
import { clientApi } from "@/lib/client-api";
import { formatMoney } from "@/lib/format";
import { type CandidateStatus, type InterviewCreate, type Tier, stageMeta } from "@ow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";

const STATUSES: CandidateStatus[] = ["active", "on_hold", "withdrawn", "rejected", "placed"];
const STATUS_LABELS: Record<CandidateStatus, string> = {
  active: "Active",
  on_hold: "On hold",
  withdrawn: "Withdrawn",
  rejected: "Rejected",
  placed: "Placed",
};
const TIER_LABEL: Record<Tier, string> = { csuite: "C-Suite", vp_director: "VP / Director", general: "General" };

/** Admin pipeline workspace: move stages, log interviews, and jump to candidate edits — all from one search. */
export function AdminSearchDetailPage() {
  const { searchId } = useParams({ strict: false }) as { searchId: string };
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "search-detail", searchId] });
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin", "search-detail", searchId],
    queryFn: () => clientApi.searchDetail(searchId),
  });

  const moveStage = useMutation({
    mutationFn: (v: { linkId: string; stage?: string; status?: CandidateStatus }) =>
      adminApi.links.moveStage(v.linkId, { stage: v.stage, status: v.status }),
    onSuccess: invalidate,
  });
  const addInterview = useMutation({
    mutationFn: (v: { linkId: string; body: InterviewCreate }) => adminApi.interviews.create(v.linkId, v.body),
    onSuccess: invalidate,
  });

  const [logFor, setLogFor] = useState<string | null>(null);
  const [logRound, setLogRound] = useState<string | undefined>(undefined);
  const [pendingLog, setPendingLog] = useState<{ linkId: string; round: string } | null>(null);

  if (isError) return <ErrorState onRetry={() => refetch()}>Couldn't load this search.</ErrorState>;
  if (isLoading || !data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const ladder = data.pipeline;
  const rankOf = (key: string) => ladder.findIndex((s) => s.key === key);
  const cards = [...data.candidates].sort((a, b) => rankOf(b.stage) - rankOf(a.stage));
  const overBudget = (n: number | null) => data.budgetMax != null && n != null && n > data.budgetMax;

  return (
    <div className="space-y-6">
      <Link
        to="/admin/searches"
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> Searches
      </Link>

      <SectionHeader title={data.title}>
        {data.launchedAt ? (
          <StatusPill value={data.status} />
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--brand-accent)] bg-[var(--brand-accent)]/15 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wide text-foreground">
            Draft · not live
          </span>
        )}
      </SectionHeader>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
        <span>Tier · {TIER_LABEL[data.tier]}</span>
        {data.ownerName ? <span>Owner · {data.ownerName}</span> : null}
        {data.function ? <span>Function · {data.function}</span> : null}
        {data.budgetMin != null || data.budgetMax != null ? (
          <span>
            Budget · {formatMoney(data.budgetMin, data.budgetCurrency)}–
            {formatMoney(data.budgetMax, data.budgetCurrency)}
          </span>
        ) : null}
      </div>

      {/* Pipeline configuration (pre-launch) or the locked, post-launch view. */}
      {data.launchedAt ? (
        <LockedPipelineView stages={data.pipeline} launchedAt={data.launchedAt} />
      ) : (
        <PipelineConfigurator searchId={data.id} initialStages={data.pipeline} />
      )}

      <section className="space-y-3">
        <SectionHeader title={`${cards.length} candidate${cards.length === 1 ? "" : "s"}`} />
        <InlineError error={moveStage.error ?? addInterview.error} />
        {cards.length === 0 ? (
          <p className="text-sm text-muted-foreground">No candidates linked to this search yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border">
            <div className="min-w-[820px] divide-y">
              {cards.map((c) => {
                const isOver = overBudget(c.salaryExpectation);
                const open = logFor === c.linkId;
                return (
                  <div key={c.linkId} className="transition-colors hover:bg-accent/30">
                    <div className="grid grid-cols-[minmax(12rem,1.6fr)_180px_140px_minmax(8.5rem,auto)_auto] items-center gap-x-4 px-3 py-2.5">
                      <div className="min-w-0">
                        <Link
                          to="/admin/candidates/$candidateId"
                          params={{ candidateId: c.candidateId }}
                          className="block truncate text-sm font-medium hover:underline"
                        >
                          {c.fullName}
                        </Link>
                        {c.currentTitle || c.currentEmployer ? (
                          <span className="block truncate text-xs text-muted-foreground">
                            {c.currentTitle ?? ""}
                            {c.currentTitle && c.currentEmployer ? " · " : ""}
                            {c.currentEmployer ?? ""}
                          </span>
                        ) : null}
                      </div>

                      <StageSelect
                        value={c.stage}
                        stages={ladder}
                        onChange={(stage) => {
                          const previous = c.stage;
                          moveStage.mutate(
                            { linkId: c.linkId, stage },
                            {
                              onSuccess: () => {
                                if (previous !== stage)
                                  setPendingLog({ linkId: c.linkId, round: stageMeta(ladder, previous).label });
                              },
                            },
                          );
                        }}
                      />

                      <Select
                        value={c.status}
                        onValueChange={(v) => moveStage.mutate({ linkId: c.linkId, status: v as CandidateStatus })}
                      >
                        <SelectTrigger className="h-9 w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <div className="whitespace-nowrap text-right text-xs leading-tight">
                        <div className="text-muted-foreground">{c.daysInStage}d in stage</div>
                        {c.salaryExpectation != null ? (
                          <div className={isOver ? "font-medium text-destructive" : "text-muted-foreground"}>
                            exp {formatMoney(c.salaryExpectation, c.currency)}
                          </div>
                        ) : null}
                      </div>

                      <Button
                        size="xs"
                        variant={open ? "secondary" : "ghost"}
                        onClick={() => {
                          setLogFor(open ? null : c.linkId);
                          setLogRound(stageMeta(ladder, c.stage).label);
                          setPendingLog(null);
                        }}
                      >
                        {open ? "Close" : "Log interview"}
                      </Button>
                    </div>

                    {pendingLog?.linkId === c.linkId ? (
                      <div className="mx-3 mb-3 flex flex-wrap items-center gap-2 rounded-md border border-[var(--brand-accent)] bg-accent/40 p-2 text-sm">
                        <span>
                          Stage moved — log the <strong>{pendingLog.round}</strong> outcome?
                        </span>
                        <Button
                          size="xs"
                          className="ml-auto"
                          onClick={() => {
                            setLogFor(c.linkId);
                            setLogRound(pendingLog.round);
                            setPendingLog(null);
                          }}
                        >
                          Log it
                        </Button>
                        <Button size="xs" variant="ghost" onClick={() => setPendingLog(null)}>
                          Dismiss
                        </Button>
                      </div>
                    ) : null}

                    {open ? (
                      <div className="px-3 pb-3">
                        <InterviewForm
                          key={`${c.linkId}-${logRound ?? ""}`}
                          defaultRound={logRound}
                          submitting={addInterview.isPending}
                          onSubmit={(body) =>
                            addInterview.mutate({ linkId: c.linkId, body }, { onSuccess: () => setLogFor(null) })
                          }
                          onCancel={() => setLogFor(null)}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
